import { isNumber } from 'lodash';
import { useCallback, useState } from 'react';
import { Module, ScopeEnum } from 'src/domain';
import {
    StyleTimelineRowUpdatedEvent,
    StyleTimelineRowUpdateEventPayload,
} from 'src/domain/events/styletimeline-row-updated.event';
import { StyleTimelineOverviewRow } from 'src/domain/table/style-timeline-overview-row';
import { useDiscardChangesModule } from 'src/hooks/discard-changes/useDiscardChangesModule';
import { useCompositePartner } from 'src/hooks/partner/useCompositePartner';
import { useChangesSnackbar } from 'src/hooks/snackbar/useChangesSnackbar';
import { useUnsavedChangesModule } from 'src/hooks/unsaved-changes';
import { useScope } from 'src/hooks/useScope';
import { UpdateStyleTimelineInput } from 'src/infrastructure/rest-api/style-timeline';
import { useApiMutation } from 'src/infrastructure/rest-api/useApi';
import { useStorefrontType } from '../useStorefrontType';

export const toStyleTimelineInput = (change: StyleTimelineOverviewRow): UpdateStyleTimelineInput => {
    const exitWeek = change.exitWeek ? Number(change.exitWeek) : null;
    const packawayStartWeek = change.packawayStartWeek;
    const packawayEndWeek = change.packawayEndWeek;
    const comment = change.comment ? change.comment : '';
    const eligibleForSale = change.eligibleForSale;
    const stylePriority = change.stylePriority ? change.stylePriority : null;

    return {
        exitWeekType: exitWeek ? 'WEEK_KEY' : change.exitWeekType,
        id: change.id,
        exitWeek: exitWeek,
        packawayStartWeek: packawayStartWeek ?? null,
        packawayEndWeek: packawayEndWeek ?? null,
        comment,
        eligibleForSale,
        stylePriority,
        chainFurnitureId: change.chainFurnitureId ?? null,
        chainFurnitureZoneId: change.chainFurnitureZoneId ?? null,
        phaseoutProfileId: change.phaseoutProfileId ?? null,
        isMandatory: change.isMandatory ?? false,
        normClosureWeek:
            isNumber(change.normClosureWeek) && change.normClosureWeek !== 0 ? change.normClosureWeek : null,
        replenishmentPhaseoutMethodId: change.replenishmentPhaseoutMethodId ?? null,
    };
};

interface SaveChangesOptions {
    showSuccessSnackbar?: boolean;
}

export const useStyleTimelineSaveChanges = (
    selectedStorefrontType?: string,
    { showSuccessSnackbar = true }: SaveChangesOptions = {}
) => {
    const discardChanges = useDiscardChangesModule(Module.StyleTimeline);
    const defaultStorefrontType = useStorefrontType();
    const storefrontType = selectedStorefrontType ?? defaultStorefrontType;
    const partnerComposite = useCompositePartner();
    const scope = useScope();
    const [isSaving, setIsSaving] = useState(false);

    const [mutateStyleTimeline] = useApiMutation('/api/styletimeline/partners', 'put', {
        update(_data, _variables, queryClient) {
            if (scope === ScopeEnum.PARTNER) {
                queryClient.invalidateQueries({
                    queryKey: [`/api/styletimeline/partners/${partnerComposite?.id}`, { storefrontType }],
                });
            }
        },
    });
    const showSnackbar = useChangesSnackbar();

    const [unsavedChanges] = useUnsavedChangesModule<StyleTimelineOverviewRow[]>(Module.StyleTimeline);

    const sendUpdateQuery = useCallback(async () => {
        const changes = unsavedChanges ?? [];
        if (changes.length <= 0) {
            // No changes to save
            return;
        }
        return mutateStyleTimeline({
            body: changes.map(toStyleTimelineInput),
        });
    }, [mutateStyleTimeline, unsavedChanges]);

    const saveStyleTimeline = useCallback(async () => {
        if (!unsavedChanges?.length) {
            return true;
        }

        setIsSaving(true);
        try {
            const updatedRows = await sendUpdateQuery();
            dispatchEvent(
                new CustomEvent<StyleTimelineRowUpdateEventPayload>(StyleTimelineRowUpdatedEvent, {
                    detail: {
                        rows: updatedRows ?? [],
                    },
                })
            );
            await discardChanges();
            if (showSuccessSnackbar) {
                showSnackbar();
            }
            return true;
        } catch (err: unknown) {
            showSnackbar(err);
            return false;
        } finally {
            setIsSaving(false);
        }
    }, [discardChanges, sendUpdateQuery, showSnackbar, showSuccessSnackbar, unsavedChanges]);

    return [saveStyleTimeline, { isSaving }] as const;
};
